import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import ConfirmationCard from './ConfirmationCard';

const DETAIL_INTENTS = {
  weather: { emoji: '📊', labelKey: 'detailIntent.weather' },
  subsidies: { emoji: '💰', labelKey: 'detailIntent.subsidies' },
  market: { emoji: '📈', labelKey: 'detailIntent.market' },
  finance: { emoji: '💶', labelKey: 'detailIntent.finance' },
};

export default function MessageBubble({ message, onShowDetail, token, onSendMessage }) {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const [imageExpanded, setImageExpanded] = useState(false);

  return (
    <>
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
        <div
          className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${
            isUser
              ? 'bg-[var(--aif-color-primary)] text-white rounded-br-md'
              : message.isError
                ? 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-bl-md'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] rounded-bl-md'
          }`}
          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
          {...(!isUser ? { 'data-ai-response': true } : {})}
        >
          {/* File attachment display */}
          {message.file && (
            <FileAttachment file={message.file} isUser={isUser} onImageClick={() => setImageExpanded(true)} />
          )}

          {isUser ? (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
          ) : message.isUpgrade ? (
            <div>
              <p className="text-sm leading-relaxed">{message.content}</p>
              {message.upgradeUrl && (
                <a
                  href={message.upgradeUrl}
                  className="inline-block mt-2 px-4 py-2 text-xs font-medium rounded-xl bg-[var(--aif-color-primary)] text-white hover:opacity-90 transition"
                >
                  {t('dashboard.upgradeCta')}
                </a>
              )}
            </div>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-pre:bg-gray-200 dark:prose-pre:bg-gray-900 prose-pre:rounded-lg">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          )}
          {!isUser && message.intent && (
            <div className="mt-2 flex items-center gap-2 text-xs text-[var(--aif-text-muted)]">
              <span className="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">{t(`contextTag.${message.intent}`, message.intent)}</span>
            </div>
          )}
          {!isUser && message.intent && DETAIL_INTENTS[message.intent] && onShowDetail && (
            <button
              onClick={(e) => { e.stopPropagation(); onShowDetail(message.intent); }}
              className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)]/20 hover:bg-[var(--aif-color-primary)]/20 transition-all active:scale-95"
            >
              <span>{DETAIL_INTENTS[message.intent].emoji}</span>
              <span>{t(DETAIL_INTENTS[message.intent].labelKey)}</span>
            </button>
          )}
          {/* Share button for AI messages */}
          {!isUser && message.content && (
            <ShareButton text={message.content} />
          )}
        </div>
      </div>

      {/* Confirmation cards (activity/task created by parser) */}
      {!isUser && message.cards && message.cards.length > 0 && (
        <div className="flex justify-start mb-1 ml-0 max-w-[85%] sm:max-w-[75%]" data-testid="confirmation-cards">
          {message.cards.map((card, i) => (
            <ConfirmationCard key={i} card={card} token={token} onShowDetail={onShowDetail} onSendMessage={onSendMessage} />
          ))}
        </div>
      )}

      {/* Fullscreen image overlay */}
      {imageExpanded && message.file && (
        <div
          className="fixed inset-0 z-[var(--aif-z-modal)] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setImageExpanded(false)}
        >
          <button
            onClick={() => setImageExpanded(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={message.file.preview || message.file.url}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </>
  );
}

function FileAttachment({ file, isUser, onImageClick }) {
  const isImage = file.type === 'image';
  const imageUrl = file.preview || file.url;

  if (isImage && imageUrl) {
    return (
      <div className="mb-2">
        <img
          src={imageUrl}
          alt={file.name || 'Upload'}
          className="max-w-[300px] w-full rounded-xl cursor-pointer hover:opacity-90 transition"
          onClick={(e) => { e.stopPropagation(); onImageClick(); }}
        />
      </div>
    );
  }

  if (file.type === 'pdf') {
    return (
      <div className={`mb-2 flex items-center gap-3 px-3 py-2.5 rounded-xl ${
        isUser ? 'bg-white/10' : 'bg-[var(--aif-bg-elevated)]'
      }`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-white/10' : 'bg-red-100 dark:bg-red-900/30'
        }`}>
          <svg className={`w-5 h-5 ${isUser ? 'text-white' : 'text-red-600 dark:text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium truncate ${isUser ? 'text-white' : 'text-[var(--aif-text-primary)]'}`}>
            {file.name || 'document.pdf'}
          </p>
          <p className={`text-xs ${isUser ? 'text-white/60' : 'text-[var(--aif-text-muted)]'}`}>PDF</p>
        </div>
        {file.url && (
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition ${
              isUser ? 'hover:bg-white/10 text-white/70' : 'hover:bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)]'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </a>
        )}
      </div>
    );
  }

  return null;
}

function ShareButton({ text }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const shareText = (text || '').slice(0, 500) + (text?.length > 500 ? '...' : '') + '\n\n— AI Farm CoPilot | airfarm.io';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
        return;
      } catch { /* user cancelled or not supported */ }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <button
      onClick={(e) => { e.stopPropagation(); handleShare(); }}
      className="mt-1.5 flex items-center gap-1 text-[10px] text-[var(--aif-text-muted)] hover:text-[var(--aif-color-primary)] transition opacity-60 hover:opacity-100"
      title={copied ? t('chat.copied') : t('chat.share')}
    >
      {copied ? (
        <span>{t('chat.copied')}</span>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          <span>{t('chat.share')}</span>
        </>
      )}
    </button>
  );
}
